import React, { Dispatch, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changeNewProductField, clearNetworkAndPricing, hideSecondaryPanel } from 'store/productManagement/reducer';
import { ProductFieldName, SelectionPermission } from 'store/productManagement/reducer.types';
import networksSvg from 'assets/icons/networks.svg';
import SectionButton from 'lib/SectionButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import { Store } from 'components/common/types/Store.types';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import {
  DirectSalesAllocationSelectedValues,
  sortNetworkCriteriaBySelectedValue,
} from 'utils/sortNetworkCriteriaBySelectedValue';
import { NetworkCriteriaType } from 'components/common/types/DirectSalesNetworks.types';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { useTransformedProductNetworks } from './useTransformedProductNetworks';
import ProductNetworksPanel from './ProductNetworksPanel';
import { hasTargets } from '../ProductTargets/utils';
import { getNetworkRangeValues, getSelectedNetworkDisplayValue } from './utils';
import { ProductNetworkChipList } from './ProductNetworkChipList';
import { PERMISSION_LABELS } from '../../consts';
import { NetworkRangeValue } from './ProductNetwork.types';

const fieldName = ProductFieldName.NETWORKS;

const getTheme = (selectedPermission?: SelectionPermission): SectionButtonTheme => {
  return selectedPermission ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED;
};

const getCategorizedValues = (
  transformedNetworks: NetworkCriteriaType[],
): Record<string, DirectSalesAllocationSelectedValues[]> => {
  if (!transformedNetworks.length) return {};

  return sortNetworkCriteriaBySelectedValue(transformedNetworks);
};

const getGroupedRangeValues = (isRange: boolean, transformedNetworks: NetworkCriteriaType[]): NetworkRangeValue => {
  if (!isRange || !transformedNetworks.length) return {};

  return getNetworkRangeValues(transformedNetworks);
};

const handleNetworkChange = ({
  transformedNetworks,
  networksToRemove,
  networkSelectedPermission,
  dispatch,
}: {
  transformedNetworks: NetworkCriteriaType[];
  networksToRemove: string[];
  networkSelectedPermission: SelectionPermission;
  dispatch: Dispatch<AnyAction>;
}): void => {
  const networksSet = new Set(networksToRemove);
  const newTransformedNetworks = transformedNetworks.filter((value) => !networksSet.has(value.name));

  if (newTransformedNetworks.length) {
    dispatch(
      changeNewProductField({
        fieldName,
        value: newTransformedNetworks,
        selectedPermission: { code: networkSelectedPermission, name: PERMISSION_LABELS[networkSelectedPermission] },
      }),
    );
  } else {
    dispatch(clearNetworkAndPricing({ fieldName }));
  }
};

const ProductNetworks: React.FC = () => {
  const dispatch = useDispatch();
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const pricingMode = useSelector((state: Store) => state.productManagement.newProduct.pricingMode?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const networkSelectedPermission = useSelector(
    (state: Store) => state.productManagement.newProduct.networks?.selectionPermission,
  );
  const budget = useSelector((state: Store) => state.productManagement.newProduct?.budget);
  const frames = useSelector((state: Store) => state.productManagement.newProduct?.frames);
  const impressions = useSelector((state: Store) => state.productManagement.newProduct?.impressions);
  const isReadOnly = useSelector(getIsReadOnly);

  const onChangePanelVisibility = useOnChangePanelVisibility();
  const transformedNetworks = useTransformedProductNetworks();
  const isProductNetworksPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.NETWORKS;
  const isRange = networkSelectedPermission === SelectionPermission.RANGE_VALUE;

  const categorizedValues = useMemo(() => getCategorizedValues(transformedNetworks), [transformedNetworks]);

  const groupedRangeValues = useMemo(
    () => getGroupedRangeValues(isRange, transformedNetworks),
    [isRange, transformedNetworks],
  );

  const isTargetSelected = hasTargets({ budget, frames, impressions });
  const selectedRangeNetworksNumber = Object.keys(groupedRangeValues).length;

  const onRemoveProductNetworkChip = (networksToRemove?: string[]): void => {
    if (!networksToRemove) {
      dispatch(clearNetworkAndPricing({ fieldName }));
      return;
    }

    if (!networksToRemove.length || !networkSelectedPermission) return;

    handleNetworkChange({ transformedNetworks, networksToRemove, networkSelectedPermission, dispatch });
  };

  return (
    <>
      <SectionButton
        dataTestId="section-button-networks"
        label="Networks"
        icon={networksSvg}
        isDisabled={
          !mediaType ||
          !channels?.length ||
          pricingMode === DirectSalesPricingMode.CPM ||
          pricingMode === DirectSalesPricingMode.PANEL
        }
        isEllipsisDisabled={isReadOnly}
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.NETWORKS)}
        theme={getTheme(networkSelectedPermission)}
        isActive={isProductNetworksPanelOpen}
        selected={
          isRange && selectedRangeNetworksNumber > 0
            ? undefined
            : getSelectedNetworkDisplayValue(categorizedValues, networkSelectedPermission)
        }
        onClear={() => {
          dispatch(clearNetworkAndPricing({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveProductNetworkChip}
        isShowLessToggleDisabled={selectedRangeNetworksNumber <= 1}
        collapsedIncludedNumber={isRange ? selectedRangeNetworksNumber : undefined}
        isRequired={!isTargetSelected && !networkSelectedPermission}
      >
        {isRange && (
          <ProductNetworkChipList groupedRangeValues={groupedRangeValues} onRemoveChip={onRemoveProductNetworkChip} />
        )}
      </SectionButton>
      {isProductNetworksPanelOpen && (
        <ProductNetworksPanel transformedNetworks={transformedNetworks} fieldName={fieldName} isReadOnly={isReadOnly} />
      )}
    </>
  );
};

export default ProductNetworks;
